/**
 * AdminAnticholinergicPage — Engine 53 Phase B firing log (May 21, 2026).
 *
 * Founder-gated print-friendly viewer for the last N encounters where the
 * Anticholinergic Burden engine fired. Lets Sivam eyeball false-positive
 * rate in real time as Archna and peers begin using the platform.
 *
 * Passcode is NEVER persisted client-side beyond the page session.
 */
import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, Printer, RefreshCw, Pill } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_PALETTE = {
  CRITICAL: "bg-rose-900 text-white",
  HIGH: "bg-rose-600 text-white",
  MODERATE: "bg-amber-500 text-white",
  LOW: "bg-slate-400 text-white",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const drugChips = (drugs) =>
  (Array.isArray(drugs) ? drugs : []).map((d, i) => (
    <span key={i} className="inline-block mr-1 mb-1 px-1.5 py-0.5 rounded bg-slate-100 border border-slate-300 text-[10px] font-mono">
      {d.name} <span className="text-slate-500">({d.score})</span>
    </span>
  ));

export default function AdminAnticholinergicPage() {
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const fetchRows = async (code) => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/anticholinergic-firings`, {
        headers: { "x-admin-passcode": code, "X-System-Test": "pai-system-test-2026" },
      });
      setRows(r.data.firings || []);
      setTotal(r.data.total || 0);
      setAuthed(true);
    } catch (e) {
      const status = e?.response?.status;
      setError(status === 401 ? "Wrong passcode." : (e?.response?.data?.error || e.message || "Unable to load."));
    } finally {
      setLoading(false);
    }
  };

  // ─── Aggregates (compute client-side so we don't add another endpoint) ─
  const severityCounts = rows.reduce((acc, r) => {
    acc[r.severity] = (acc[r.severity] || 0) + 1;
    return acc;
  }, {});
  const avgScore = rows.length
    ? (rows.reduce((s, r) => s + (r.anticholinergic_score || 0), 0) / rows.length).toFixed(1)
    : "—";

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="admin-anti-gate">
        <div className="bg-white border-2 border-slate-300 rounded-xl shadow-sm p-8 max-w-md w-full">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-slate-700" />
            <h1 className="text-lg font-bold text-slate-900">Engine 53 Phase B — Firing Log</h1>
          </div>
          <p className="text-xs text-slate-600 mb-5">
            Anticholinergic Burden engine. Founder-gated review of every encounter where the new May 21, 2026 engine fired.
            Sandbox / synthetic data only — no PHI.
          </p>
          <Label className="text-xs font-bold text-slate-800">Admin passcode</Label>
          <Input
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && passcode) fetchRows(passcode); }}
            placeholder="••••••"
            className="mt-1"
            data-testid="admin-anti-passcode-input"
            autoFocus
          />
          {error && (
            <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid="admin-anti-error">
              {error}
            </div>
          )}
          <Button
            onClick={() => passcode && fetchRows(passcode)}
            disabled={!passcode || loading}
            className="mt-5 w-full bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="admin-anti-unlock-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Unlock
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 sm:p-10" data-testid="admin-anti-page">
      <div className="max-w-7xl mx-auto">
        <header className="flex flex-wrap items-end justify-between gap-3 mb-6 no-print">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 flex items-center gap-2">
              <Pill className="w-3 h-3" />
              P.AI Global Health · Founder Admin · Engine 53 Phase B
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mt-0.5" data-testid="admin-anti-title">
              Anticholinergic Burden Firings — {total} {total === 1 ? "encounter" : "encounters"}
            </h1>
            <p className="text-xs text-slate-500 mt-1">
              AGS Beers 2023 + ACB Scale (Boustani). Newest first. Sandbox data only.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => fetchRows(passcode)} disabled={loading} data-testid="admin-anti-refresh-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
            <Button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="admin-anti-print-btn">
              <Printer className="w-4 h-4 mr-2" />
              Print / Save PDF
            </Button>
          </div>
        </header>

        {/* Aggregate strip */}
        {rows.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="admin-anti-aggregates">
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <div className="text-[10px] uppercase font-bold text-slate-500">Total</div>
              <div className="text-2xl font-bold text-slate-900">{total}</div>
            </div>
            {["CRITICAL", "HIGH", "MODERATE", "LOW"].map((s) => (
              <div key={s} className="bg-white border border-slate-200 rounded-lg p-3">
                <div className={`text-[10px] uppercase font-bold inline-block px-1.5 py-0.5 rounded ${SEVERITY_PALETTE[s] || "bg-slate-300 text-slate-900"}`}>{s}</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">{severityCounts[s] || 0}</div>
              </div>
            ))}
          </div>
        )}

        {rows.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-10 text-center text-slate-500" data-testid="admin-anti-empty">
            No anticholinergic burden firings yet. As soon as a patient or validator submits an intake with any Beers 2023 score-1+ drug, the row will appear here.
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-anti-table">
                <thead>
                  <tr className="bg-slate-900 text-white text-left">
                    <th className="px-3 py-2 font-bold whitespace-nowrap">When</th>
                    <th className="px-3 py-2 font-bold">Encounter</th>
                    <th className="px-3 py-2 font-bold">Age · Sex</th>
                    <th className="px-3 py-2 font-bold">Severity</th>
                    <th className="px-3 py-2 font-bold whitespace-nowrap">ACB Score</th>
                    <th className="px-3 py-2 font-bold">Detected Drugs</th>
                    <th className="px-3 py-2 font-bold">Chief Complaint</th>
                    <th className="px-3 py-2 font-bold">ICD-10</th>
                    <th className="px-3 py-2 font-bold">Validator</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.encounterId} className="border-b border-slate-100 align-top" data-testid={`admin-anti-row-${r.encounterId}`}>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtDate(r.createdAt || r.visitDate)}</td>
                      <td className="px-3 py-2 font-mono text-slate-700">{r.encounterId}</td>
                      <td className="px-3 py-2 text-slate-800 whitespace-nowrap">{r.age || "—"} · {r.sex || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${SEVERITY_PALETTE[r.severity] || "bg-slate-300 text-slate-900"}`}>{r.severity || "—"}</span>
                      </td>
                      <td className="px-3 py-2 text-slate-800 font-mono">{r.anticholinergic_score ?? "—"}</td>
                      <td className="px-3 py-2 max-w-[260px]">{drugChips(r.anticholinergic_drugs)}</td>
                      <td className="px-3 py-2 text-slate-700 italic max-w-[220px]">{r.chiefComplaint || "—"}</td>
                      <td className="px-3 py-2 text-slate-700 font-mono text-[10px]">{Array.isArray(r.icd10) && r.icd10.length ? r.icd10.join(", ") : "—"}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {r.clinicalValidator && r.validatorName ? (
                          <>
                            <div className="font-bold text-slate-900">{r.validatorName}</div>
                            <div className="text-[10px] text-slate-500">{r.validatorEmail}</div>
                          </>
                        ) : (
                          <span className="text-slate-400 italic">patient</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="text-[10px] text-slate-400 mt-4 no-print">
          Source: <code>GET /api/admin/anticholinergic-firings</code> (passcode-gated, last 50, sorted by createdAt descending).
          Avg ACB score across visible rows: <strong>{avgScore}</strong>.
        </p>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff; }
          table { font-size: 9.5px; }
          th { background: #0f172a !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
